// @flow
import * as React from 'react';
import classNames from 'classnames';
import ContentLoader from 'react-content-loader';
// eslint-disable-next-line import/named
import type { IntlShape } from 'react-intl';

// Constants
import { TRANSPORTATION, TRANSPORTATION_LABEL } from 'constants/transportation';

//  Components
import { Container, Row, Col } from 'reactstrap';
import Panel from 'components/atoms/Panel';
import LoaderHOC from 'components/atoms/LoaderHOC';
import DataSlot from 'components/atoms/DataSlot';
import ParseHTML from 'components/atoms/ParseHTML';
import UnavailableBanner from 'components/atoms/UnavailableBanner';

// Utils
import { isEmpty } from 'utils/string';
import { setTransportationLabel } from 'utils/transportationLabel';

import TransportationsLoader from './PanelDropOffLoader';
import './PanelDropOff.scss';

const { DROPOFF, WAITER, SHUTTLE, RENTAL, LOANER, RIDESHARE } = TRANSPORTATION;
const {
  DROPOFF_LABEL,
  WAITER_LABEL,
  SHUTTLE_LABEL,
  RENTAL_LABEL,
  LOANER_LABEL,
  RIDESHARE_LABEL
} = TRANSPORTATION_LABEL;
const ns = 'panel-drop-off';

export type Props = {
  transportOptions: Array<Object>,
  amenities: Array<string>,
  setTransportationOption: (transportation: string) => Object,
  transportation: string,
  transpOptionInfo: Object,
  dealerName: string,
  isTransportationAvailable: (transportation?: string) => Boolean,
  getTranslate: (key?: string) => string,
  intl: IntlShape,
  sendTransportationOptionsStart: () => void
};

class PanelDropOff extends React.Component<Props> {
  isPanelYesEnabled() {
    const { transportOptions } = this.props;
    return Boolean(
      transportOptions.find(({ data }) => data !== DROPOFF && data !== WAITER)
    );
  }

  transportationSelect(transportation: string) {
    const {
      sendTransportationOptionsStart,
      setTransportationOption
    } = this.props;

    setTransportationOption(transportation);
    sendTransportationOptionsStart();
  }

  contentLoader = () => {
    return (
      <Col className={'pl-0'} xs={12} sm={6}>
        <TransportationsLoader />
      </Col>
    );
  };

  showTransportationDetails(option: string, description: string) {
    const { intl } = this.props;
    return (
      <div className={`${ns}__transportation-details`}>
        <div className={`${ns}__transportation-details-title`}>
          {intl.formatMessage(
            { id: 'c8.drop.off.transportation.info' },
            { Transportation: option.toUpperCase() }
          )}
        </div>
        <ParseHTML
          html={description}
          className={`${ns}__transportation-details-body`}
        />
      </div>
    );
  }

  getTransportationAdditionalInfo(transportations: Array<Object>) {
    const {
      transportation,
      amenities,
      dealerName,
      getTranslate,
      intl
    } = this.props;
    const selectedTransportation = transportations.find(
      ({ data }) => data === transportation
    );

    if (selectedTransportation) {
      switch (transportation) {
        case WAITER:
          if (
            amenities.length > 0 ||
            !isEmpty(getTranslate('c8.transportation.options.desc.waiter'))
          ) {
            return (
              <div>
                {!isEmpty(
                  getTranslate('c8.transportation.options.desc.dropoff')
                )
                  ? this.showTransportationDetails(
                      !isEmpty(getTranslate('c8.transportation.waiter'))
                        ? getTranslate('c8.transportation.waiter')
                        : WAITER_LABEL,
                      getTranslate('c8.transportation.options.desc.waiter')
                    )
                  : ''}
                {amenities.length > 0 && (
                  <div className={`${ns}__amenities`}>
                    <div className={`${ns}__amenities-title`}>
                      {intl.formatMessage(
                        { id: 'c8.drop.off.amenities.at' },
                        { dealerName }
                      )}
                    </div>
                    <div className={`${ns}__amenities-list`}>
                      <ul>
                        {amenities.map(data => {
                          return (
                            <li key={data}>
                              {intl.formatMessage({ id: data })}
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  </div>
                )}
              </div>
            );
          }
          break;
        case DROPOFF:
          if (
            !isEmpty(getTranslate('c8.transportation.options.desc.dropoff'))
          ) {
            return this.showTransportationDetails(
              !isEmpty(getTranslate('c8.transportation.drop.off'))
                ? getTranslate('c8.transportation.drop.off')
                : DROPOFF_LABEL,
              getTranslate('c8.transportation.options.desc.dropoff')
            );
          }
          break;
        case SHUTTLE:
          if (
            !isEmpty(getTranslate('c8.transportation.options.desc.shuttle'))
          ) {
            return this.showTransportationDetails(
              !isEmpty(getTranslate('c8.transportation.shuttle'))
                ? getTranslate('c8.transportation.shuttle')
                : SHUTTLE_LABEL,
              getTranslate('c8.transportation.options.desc.shuttle')
            );
          }
          break;
        case RENTAL:
          if (!isEmpty(getTranslate('c8.transportation.options.desc.rental'))) {
            return this.showTransportationDetails(
              !isEmpty(getTranslate('c8.transportation.rental'))
                ? getTranslate('c8.transportation.rental')
                : RENTAL_LABEL,
              getTranslate('c8.transportation.options.desc.rental')
            );
          }
          break;
        case LOANER:
          if (!isEmpty(getTranslate('c8.transportation.options.desc.loaner'))) {
            return this.showTransportationDetails(
              !isEmpty(getTranslate('c8.transportation.loaner'))
                ? getTranslate('c8.transportation.loaner')
                : LOANER_LABEL,
              getTranslate('c8.transportation.options.desc.loaner')
            );
          }
          break;
        case RIDESHARE:
          if (
            !isEmpty(getTranslate('c8.transportation.options.desc.rideshare'))
          ) {
            return this.showTransportationDetails(
              !isEmpty(getTranslate('c8.transportation.rideshare'))
                ? getTranslate('c8.transportation.rideshare')
                : RIDESHARE_LABEL,
              getTranslate('c8.transportation.options.desc.rideshare')
            );
          }
          break;
        default:
          return null;
      }
    }
    return null;
  }

  getTransportationPanel(title: string) {
    const {
      transportation,
      isTransportationAvailable,
      transportOptions,
      transpOptionInfo,
      intl
    } = this.props;
    const isPanelYesEnabled = this.isPanelYesEnabled();
    const titleNoText = intl.formatMessage({ id: 'c8.drop.off.no' });
    const titleYesText = intl.formatMessage({ id: 'c8.drop.off.yes' });
    const transportations = transportOptions
      .concat(
        transpOptionInfo && !isTransportationAvailable(transpOptionInfo.data)
          ? transpOptionInfo
          : []
      )
      .filter(({ data }) => {
        return title === titleNoText
          ? data === DROPOFF || data === WAITER
          : data !== DROPOFF && data !== WAITER;
      });
    let panelAriaLabel = '';

    if (title === titleNoText) {
      panelAriaLabel = intl.formatMessage({
        id: 'c8.aria.no.ride.needed',
        defaultMessage: 'No Ride Needed'
      });
    } else if (title === titleYesText) {
      panelAriaLabel = intl.formatMessage({
        id: 'c8.aria.ride.needed',
        defaultMessage: 'Ride Needed'
      });
    }

    if (transportations.length === 0) {
      return null;
    }

    return (
      <Col xs={12} sm={isPanelYesEnabled ? 6 : 12}>
        <Panel tabIndex={0} aria-label={panelAriaLabel}>
          {isPanelYesEnabled && <div className={`${ns}__label`}>{title}</div>}
          <div className={`${ns}__slot d-flex flex-row flex-wrap`}>
            {transportations.map(({ label, data }) => (
              <DataSlot
                key={data}
                value={setTransportationLabel(label, intl)}
                bsStyle="primary"
                onClick={() => {
                  this.transportationSelect(data);
                }}
                active={data === transportation}
                disabled={!isTransportationAvailable(data)}
              />
            ))}
          </div>
          {this.getTransportationAdditionalInfo(transportations)}
        </Panel>
      </Col>
    );
  }

  render() {
    const { intl } = this.props;
    const isPanelYesEnabled = this.isPanelYesEnabled();
    const titlePage = classNames({
      [`${ns}__title--paragraph`]: isPanelYesEnabled,
      [`${ns}__title`]: !isPanelYesEnabled
    });

    return (
      <Container className={ns}>
        <LoaderHOC
          serviceId={'metaVehicle|apptOptions'}
          placeHolderType={() => (
            <ContentLoader height={30}>
              <rect className="placeholder-rect-title" />
            </ContentLoader>
          )}
          component={() => (
            <Row>
              <Col className={'d-flex flex-column'}>
                {isPanelYesEnabled && (
                  <span className={titlePage}>
                    {intl.formatMessage({
                      id: 'c8.drop.off.while.we.are.servicing.your.vehicle'
                    })}
                  </span>
                )}
                <h1 className={`${ns}__title`}>
                  {isPanelYesEnabled
                    ? intl.formatMessage({
                        id: 'c8.drop.off.do.you.need.a.ride'
                      })
                    : intl.formatMessage({
                        id: 'c8.drop.off.while.we.are.servicing.your.vehicle.2'
                      })}
                </h1>
              </Col>
            </Row>
          )}
        />
        <UnavailableBanner />
        <Row>
          <LoaderHOC
            serviceId={'metaVehicle|apptOptions'}
            placeHolderType={() => this.contentLoader()}
            component={() =>
              this.getTransportationPanel(
                intl.formatMessage({ id: 'c8.drop.off.no' })
              )
            }
          />
          <LoaderHOC
            serviceId={'metaVehicle|apptOptions'}
            placeHolderType={() => this.contentLoader()}
            component={() =>
              this.getTransportationPanel(
                intl.formatMessage({ id: 'c8.drop.off.yes' })
              )
            }
          />
        </Row>
        <Row className={`${ns}__note d-block align-items-center`}>
          <span className={`${ns}__note--bold`}>
            {`${intl.formatMessage({ id: 'c8.drop.off.note' })} `}{' '}
          </span>
          {intl.formatMessage({
            id: 'c8.drop.off.your.selection.may.affect.appointment.availability'
          })}
        </Row>
      </Container>
    );
  }
}

export default PanelDropOff;



// WEBPACK FOOTER //
// ./src/components/molecules/PanelDropOff/PanelDropOff.js